import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"
import LicensePlanViewInfo from "@/v2-backoffice-client/license/plan/view/LicensePlanViewInfo"
import LicensePlanViewToolbar from "@/v2-backoffice-client/license/plan/view/LicensePlanViewToolbar"
import LicensePlanRevenue from "@/v2-backoffice-client/license/plan/view/LicensePlanRevenue"
import LicensePlanCustomerTable from "@/v2-backoffice-client/license/plan/view/LicensePlanCustomerTable"
import LicensePlanSeatHolderTable from "@/v2-backoffice-client/license/plan/view/LicensePlanSeatHolderTable"
import LicensePlanFeatures from "@/v2-backoffice-client/license/plan/view/LicensePlanFeatures"
import LicensePlanMigrationTable from "@/v2-backoffice-client/license/plan/view/LicensePlanMigrationTable"

// The license panel (?v=plan:<id>). Deliberately fed by the same getLicensePlans
// list the board renders — the catalog is ~30 rows, board→panel opens from
// the Apollo cache, and the Revenue tab needs every license's MRR anyway to
// place this one in the catalog (rank, share, comparison bars). The tab
// badges read the license row's own counts (same membership rules as the tab
// procs — see Get_Lic_Plans); each tab owns its list query and loads it when
// first opened.
function LicensePlanViewSheet() {
  const { id } = useAppViewSheetOpener()
  const planId = Number(id)

  const {
    data: plansData,
    previousData: previousPlansData,
    loading: plansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = useLicensePlansQuery({ skip: !planId })

  // previousData keeps the panel rendered through post-save evictions,
  // when the cache is briefly empty and data undefined.
  const plans = (plansData ?? previousPlansData)?.getLicensePlans ?? []
  const plan = plans.find((candidate) => candidate.Id === planId)

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={plansLoading && !plan}>
          {plan?.Name}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <LicensePlanViewInfo
          plan={plan}
          plans={plans}
          isLoading={plansLoading && !plan}
        />
        <LicensePlanViewToolbar plan={plan} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="license-plan-view-tabs"
        defaultValue="revenue"
        // Only gate the tabs while there is nothing to show: with
        // cache-and-network the list query reports loading on every
        // revalidation (mutations refetch active queries), and gating on
        // that would unmount and flash the whole panel.
        isLoading={plansLoading && !plan}
        error={plansError}
        isEmpty={!plansLoading && !plan}
        emptyLabel="License not found."
      >
        <Tabs.List>
          <Tabs.Trigger value="revenue">Revenue</Tabs.Trigger>
          <Tabs.Trigger value="customers" count={plan?.CustomerCount}>
            Customers
          </Tabs.Trigger>
          <Tabs.Trigger value="seats" count={plan?.NamedSeats}>
            Named Seats
          </Tabs.Trigger>
          <Tabs.Trigger value="features" count={plan?.PackageCount}>
            Packages
          </Tabs.Trigger>
          {/* Only deprecated plans have a migration to run; the badge is the
              remaining worklist (licenses still live on the plan). */}
          {plan?.DeprecatedAt && (
            <Tabs.Trigger value="migration" count={plan.LiveLicenseCount}>
              Migration
            </Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value="revenue" className="min-h-0 overflow-auto">
          {plan && <LicensePlanRevenue plan={plan} plans={plans} />}
        </Tabs.Content>

        <Tabs.Content value="customers" className="min-h-0">
          {plan && (
            <LicensePlanCustomerTable
              planId={plan.Id}
              exportFileName={`license-${plan.Id}-customers`}
              onRefresh={() => void refetchPlans()}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="seats" className="min-h-0">
          {plan && (
            <LicensePlanSeatHolderTable
              planId={plan.Id}
              exportFileName={`license-${plan.Id}-named-seats`}
              onRefresh={() => void refetchPlans()}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="features" className="min-h-0 flex-1">
          {plan && (
            <LicensePlanFeatures planId={plan.Id} planName={plan.Name} />
          )}
        </Tabs.Content>

        {/* Content renders even when the trigger doesn't: a deep link (or a
            leftover tab param after reactivating) must land on an honest
            explanation, not a blank pane. */}
        <Tabs.Content value="migration" className="min-h-0">
          {plan?.DeprecatedAt ? (
            <LicensePlanMigrationTable
              planId={plan.Id}
              suggestedToPlanId={plan.SuccessorPlanId}
              onRefresh={() => void refetchPlans()}
            />
          ) : (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              This license is not deprecated — there is no migration to run.
              Deprecating it (toolbar) opens the worklist here.
            </p>
          )}
        </Tabs.Content>
      </Tabs>
    </>
  )
}

export default LicensePlanViewSheet
